import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { LayoutList, Network, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AgentMap } from '@/components/AgentMap';
import { PageHeader } from '@/components/Layout';
import { Badge, Button, Card, Dialog, Empty, ErrorNote, Field, Input, Loading, Select, Tabs, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtMs, fmtUsd, fmtWhen } from '@/lib/format';
import { canEdit, rk, useAgents, useMe, useProjects } from '@/lib/hooks';

export const Agents = () => {
  const me = useMe();
  const projects = useProjects();
  const [project, setProject] = useState<string>('all');
  const agents = useAgents(project === 'all' ? undefined : project);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<'list' | 'map'>(() => {
    try {
      return localStorage.getItem('agents.view') === 'map' ? 'map' : 'list';
    } catch {
      return 'list';
    }
  });
  const pickView = (v: 'list' | 'map') => {
    setView(v);
    try {
      localStorage.setItem('agents.view', v);
    } catch {
      /* private mode: the choice just isn't remembered */
    }
  };

  return (
    <>
      <PageHeader
        title="Agents"
        subtitle="Prompts, models and tools for every AI feature. Publishing takes effect within 30 seconds, without a deploy."
        actions={
          canEdit(me.data) && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="size-4" /> New agent
            </Button>
          )
        }
      />
      <div className="flex items-end justify-between gap-3">
        <div className="min-w-0 flex-1">
          <Tabs
            value={project}
            onChange={setProject}
            items={[{ value: 'all', label: 'All' }, ...(projects.data?.projects ?? []).map((p) => ({ value: p.slug, label: p.name }))]}
          />
        </div>
        <div className="mb-1.5 flex rounded-md border border-line bg-panel p-0.5" role="group" aria-label="View">
          {(
            [
              ['list', LayoutList, 'List'],
              ['map', Network, 'Map'],
            ] as const
          ).map(([v, Icon, label]) => (
            <button
              key={v}
              aria-pressed={view === v}
              onClick={() => pickView(v)}
              className={`flex items-center gap-1 rounded px-2 py-1 text-[12px] ${view === v ? 'bg-ink text-white' : 'text-ink-2 hover:text-ink'}`}
            >
              <Icon className="size-3.5" /> {label}
            </button>
          ))}
        </div>
      </div>
      {view === 'map' ? (
        <Card className="mt-4 overflow-hidden">
          <AgentMap key={project} project={project === 'all' ? undefined : project} />
        </Card>
      ) : (
        <Card className="mt-4 overflow-x-auto">
          {agents.isLoading ? (
            <Loading />
          ) : agents.error ? (
            <div className="p-4">
              <ErrorNote error={agents.error} />
            </div>
          ) : !agents.data?.agents.length ? (
            <Empty title="No agents yet">Create an agent, publish it, then call it from product code with its slug.</Empty>
          ) : (
            <table className="w-full min-w-[860px] text-[13px]">
              <thead>
                <tr className="border-b border-line text-left text-[11px] tracking-wide text-ink-3 uppercase">
                  <th className="px-4 py-2 font-medium">Agent</th>
                  <th className="px-3 py-2 font-medium">Model</th>
                  <th className="px-3 py-2 font-medium">Live</th>
                  <th className="px-3 py-2 text-right font-medium">Requests 24h</th>
                  <th className="px-3 py-2 text-right font-medium">Errors</th>
                  <th className="px-3 py-2 text-right font-medium">p95</th>
                  <th className="px-3 py-2 text-right font-medium">Cost 24h</th>
                  <th className="px-4 py-2 text-right font-medium">Updated</th>
                </tr>
              </thead>
              <tbody>
                {agents.data.agents.map((a) => (
                  <tr key={a.id} className="border-b border-line-2 last:border-0 hover:bg-line-2/50">
                    <td className="px-4 py-2.5">
                      <Link to="/agents/$agentId" params={{ agentId: a.id }} className="block">
                        <div className="font-medium">{a.name}</div>
                        <div className="mono text-ink-3">{a.slug}</div>
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-ink-2">{a.kind === 'flow' ? <Badge tone="brand">flow</Badge> : a.model ? a.model.name : '—'}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {a.publishedVersion ? <Badge tone="ok">v{a.publishedVersion}</Badge> : <Badge>not published</Badge>}
                        {a.canaryPercent > 0 && <Badge tone="brand">canary {a.canaryPercent}%</Badge>}
                        {a.hasDraft && <Badge tone="warn">draft</Badge>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{a.stats24h.requests.toLocaleString()}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      {a.stats24h.errors ? <span className="text-bad">{a.stats24h.errors}</span> : <span className="text-ink-3">0</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmtMs(a.stats24h.p95Ms)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{fmtUsd(a.stats24h.costUsd)}</td>
                    <td className="px-4 py-2.5 text-right text-ink-3">{fmtWhen(a.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
      <NewAgentDialog open={creating} onClose={() => setCreating(false)} defaultProject={project === 'all' ? projects.data?.projects[0]?.slug : project} />
    </>
  );
};

const NewAgentDialog = ({ open, onClose, defaultProject }: { open: boolean; onClose: () => void; defaultProject?: string }) => {
  const projects = useProjects();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ project: '', slug: '', name: '', description: '', kind: 'agent' as 'agent' | 'flow' });
  const project = form.project || defaultProject || '';
  const create = useMutation({
    mutationFn: () => api<{ agent: { id: string } }>('/v1/agents', { body: { ...form, project } }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: rk('agents') });
      toast.success('Agent created — edit the draft, then publish');
      onClose();
      setForm({ project: '', slug: '', name: '', description: '', kind: 'agent' });
      void navigate({ to: '/agents/$agentId', params: { agentId: res.agent.id } });
    },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New agent"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!form.slug || !form.name || !project} onClick={() => create.mutate()}>
            Create draft
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Field label="Project">
          <Select value={project} onChange={(e) => setForm({ ...form, project: e.target.value })}>
            {(projects.data?.projects ?? []).map((p) => (
              <option key={p.id} value={p.slug}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Type">
          <div className="flex gap-4 text-[13px]">
            {(
              [
                ['agent', 'Agent — one prompt and model'],
                ['flow', 'Flow — several agents wired together'],
              ] as const
            ).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="radio" className="accent-ink" checked={form.kind === k} onChange={() => setForm({ ...form, kind: k })} />
                {label}
              </label>
            ))}
          </div>
        </Field>
        <Field label="Name">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="CV parser" autoFocus />
        </Field>
        <Field label="Slug" hint="How code calls it. Can't be changed later.">
          <Input
            className="mono"
            value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '-') })}
            placeholder={`${project || 'apply'}.cv-parser`}
          />
        </Field>
        <Field label="Description">
          <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What it does and where it's used" />
        </Field>
        {create.error ? <ErrorNote error={create.error} /> : null}
      </div>
    </Dialog>
  );
};
